// Контракт API пульта (задача D1): общие формы и словари — моменты, ошибки, роли, ступени, режимы, бюджет, курс, тоны.
//
// Правило контракта: файлы `src/console/contract/*` импортируют только `zod` и друг друга (`./*.js`), типы — `z.infer`.
// Так эти файлы подключаются и в сервер, и в пульт (`web/`, псевдоним `@contract/*`). Перечисления здесь — копии
// серверных (`STAGE_ORDER` A14, роли A2, режимы A9, состояния бюджета A10); совпадение держит `tests/console/contract.test.ts`.
//
// Тексты — по-русски; перед «%» и «₽» — неразрывный пробел U+00A0.
import { z } from 'zod';

const NBSP = ' ';

/** Момент времени: ISO UTC с «Z», как его пишет `toISOString()`. Время для людей пульт пишет по МСК сам. */
export const MomentSchema = z.iso.datetime();
export type Moment = z.infer<typeof MomentSchema>;

/** Календарный день по МСК: `ГГГГ-ММ-ДД`. */
export const DaySchema = z.iso.date();

/** Календарный месяц по МСК: `ГГГГ-ММ`. */
export const MonthSchema = z.string().regex(/^\d{4}-(?:0[1-9]|1[0-2])$/);

/** Тело любого ответа-ошибки: `code` — для кода пульта, `messageRu` — для человека (что не так и что делать). */
export const ApiErrorBodySchema = z.object({ error: z.object({ code: z.string(), messageRu: z.string() }) });
export type ApiErrorBody = z.infer<typeof ApiErrorBodySchema>;

/** Роли пульта: «Админ», «Просмотр», «Редактор базы» (в v1 видит пульт как «Просмотр»). */
export const ROLES = ['admin', 'viewer', 'editor'] as const;
export const RoleSchema = z.enum(ROLES);
export type Role = z.infer<typeof RoleSchema>;

/** Ступени запуска по порядку (A14): тень → подсказки → 10 % → 50 % → 100 % новых чатов. */
export const STAGE_ORDER = ['shadow', 'assist', 'live10', 'live50', 'live100'] as const;
export const StageSchema = z.enum(STAGE_ORDER);
export type Stage = z.infer<typeof StageSchema>;

/** Режим Анны и канала (A9): выключена, тень, подсказки, бой. */
export const CHANNEL_MODES = ['off', 'shadow', 'assist', 'live'] as const;
export const ChannelModeSchema = z.enum(CHANNEL_MODES);
export type ChannelMode = z.infer<typeof ChannelModeSchema>;

/** Режим диалога: меньший из общего и канального, без «выключена» (диалог в выключенном режиме не ведётся). */
export const DIALOG_MODES = ['shadow', 'assist', 'live'] as const;
export const ModeSchema = z.enum(DIALOG_MODES);
export type Mode = z.infer<typeof ModeSchema>;

/** Состояние бюджета (A10): в порядке, порог тревоги, экономный режим, исчерпан. */
export const BUDGET_STATES = ['ok', 'warn', 'economy', 'exhausted'] as const;
export const BudgetStateSchema = z.enum(BUDGET_STATES);
export type BudgetState = z.infer<typeof BudgetStateSchema>;

/**
 * Курс доллара (A10): `date` — день МСК, на который курс запрашивали у ЦБ; `null` — курс не от ЦБ на этот день
 * (запасной). `source` — откуда курс: ЦБ, сохранённый, запасной.
 */
export const FX_SOURCES = ['cbr', 'saved', 'fallback'] as const;
export const FxSchema = z.object({ usdRub: z.number().positive(), date: DaySchema.nullable(), source: z.enum(FX_SOURCES) });
export type Fx = z.infer<typeof FxSchema>;

/**
 * Тон точки статуса — общий для сервера и пульта: `live` — синяя (ведёт, работает), `sales` — зелёная (в продажи,
 * в порядке), `human` — янтарная (к человеку, внимание, экономит), `error` — красная (сбой), `reject` — серая (отказ,
 * остановлена), `neutral` — полая (ждёт, мало данных).
 */
export const STATUS_TONES = ['live', 'sales', 'human', 'error', 'reject', 'neutral'] as const;
export const StatusToneSchema = z.enum(STATUS_TONES);
export type StatusTone = z.infer<typeof StatusToneSchema>;

/** Ступени словами — так их называют везде в пульте. */
export const STAGE_RU: Readonly<Record<Stage, string>> = {
  shadow: 'Тень',
  assist: 'Подсказки',
  live10: `10${NBSP}% новых чатов`,
  live50: `50${NBSP}% новых чатов`,
  live100: `100${NBSP}% новых чатов`,
};

/** Ступени коротко — внутри фразы: «Ворота на 10 %». */
export const STAGE_SHORT_RU: Readonly<Record<Stage, string>> = {
  shadow: 'тень',
  assist: '«Подсказки»',
  live10: `10${NBSP}%`,
  live50: `50${NBSP}%`,
  live100: `100${NBSP}%`,
};

/** Подпись группы «К следующей ступени» по следующей ступени (из тени по пути запуска — «К 100 %»). */
export const STAGE_NEXT_GROUP_RU: Readonly<Record<Exclude<Stage, 'shadow'>, string>> = {
  assist: 'К «Подсказкам»',
  live10: `К 10${NBSP}%`,
  live50: `К 50${NBSP}%`,
  live100: `К 100${NBSP}%`,
};

/** Подпись группы «Понадобится» по ступени, с которой проверка нужна. */
export const STAGE_LATER_GROUP_RU: Readonly<Record<Exclude<Stage, 'shadow'>, string>> = {
  assist: 'Понадобится с «Подсказок»',
  live10: `Понадобится с 10${NBSP}%`,
  live50: `Понадобится с 50${NBSP}%`,
  live100: `Понадобится со 100${NBSP}%`,
};

/** Подпись группы «Понадобится» для проверок, нужных только после запуска (`neededFrom: null`). */
export const LATER_AFTER_LAUNCH_RU = 'Понадобится после запуска';

/** Режим канала словами. */
export const CHANNEL_MODE_RU: Readonly<Record<ChannelMode, string>> = {
  off: 'выключен',
  shadow: 'тень',
  assist: 'подсказки',
  live: 'бой',
};

/** Роль словами. */
export const ROLE_RU: Readonly<Record<Role, string>> = { admin: 'Админ', viewer: 'Просмотр', editor: 'Редактор базы' };
